import type { ApolloClient, OperationVariables, QueryOptions } from '@apollo/client';

const trackedQueries = new Set<Promise<unknown>>();

/**
  This is a thin wrapper around `apolloClient.query`.
  It helps prevent error "Store reset while query was in flight (not completed in link chain)" ("Invariant Violation: 21").
  It's used the same way and it should function identically as the `query` method.

  The aforementioned error happens when a _non-observable_ query is in flight while the Apollo store is being reset.
  Queries performed with `useQuery`, `useLazyQuery` and some other methods are observables;
  they report their status back to the consumer, and the Apollo client knows how handle them when resetting the store.
  However, queries started imperatively with the `query` method only return a Promise and are not tracked in the same way.
  Still, an error will be thrown if they are in flight because of the possibility that their result would be incorrect.
  For example, we could perform query `foo` for some logged in user,
  then while it's in flight, we log the user out and reset the store.
  Once it finishes, this query's result will now be invalid because it's possibly specific to the user who is no longer logged in,
  but Apollo has no way to notify the function awaiting the Promise that this is the case.
  Apollo also doesn't provide us with any built-in way to wait for all in-flight queries to finish before resetting the store,
  so we have to build one ourselves.
 */
export const trackedQuery = <T, TVariables extends OperationVariables>(
  apolloClient: ApolloClient<unknown>,
  queryOptions: QueryOptions<TVariables, T>,
) => {
  // This is the wrapper around the `query` method that replaces it.
  const query = apolloClient.query(queryOptions).then((results) => {
    trackedQueries.delete(query);
    return results;
  });

  trackedQueries.add(query);

  return query;
};

export const resolveTrackedQueries = () => Promise.all(trackedQueries.values());
